import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Upload,
  CheckCircle2,
  FileText,
  X,
  AlertCircle,
  Sparkles,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/atoms/Button';

const apiServerUrl = import.meta.env.VITE_API_SERVER_URL;

interface TokenStatus {
  email: string;
  has_cv: boolean;
  cv_filename: string | null;
}

export const ChallengeCvUpload: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<'loading' | 'ready' | 'invalid'>('loading');
  const [tokenInfo, setTokenInfo] = useState<TokenStatus | null>(null);
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!token) return;
    fetch(`${apiServerUrl}/api/challenge/cv/${token}`)
      .then((res) => {
        if (!res.ok) throw new Error('Invalid');
        return res.json();
      })
      .then((data: TokenStatus) => {
        setTokenInfo(data);
        setStatus('ready');
      })
      .catch(() => setStatus('invalid'));
  }, [token]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setCvFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) setCvFile(file);
  };

  const handleUpload = async () => {
    if (!cvFile || !token) return;
    setIsUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('cv', cvFile);

      const res = await fetch(`${apiServerUrl}/api/challenge/cv/${token}`, {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        setError(data?.detail || 'Upload failed. Please try again.');
        return;
      }

      setIsUploaded(true);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#171D27] text-white flex flex-col">
      {/* Background glows */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] rounded-full opacity-[0.07]"
          style={{ background: 'radial-gradient(circle, #FAC8D2 0%, transparent 70%)' }} />
        <div className="absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] rounded-full opacity-[0.07]"
          style={{ background: 'radial-gradient(circle, #E5CBF6 0%, transparent 70%)' }} />
      </div>

      {/* Header */}
      <header className="relative z-20">
        <nav className="flex items-center px-4 sm:px-6 lg:px-12 h-16 sm:h-20">
          <div className="flex items-center gap-2">
            <img src="/logo/light-logo.png" alt="Invirtus" className="w-8 h-8 lg:w-10 lg:h-10" />
            <span className="text-white text-2xl sm:text-3xl lg:text-4xl font-bold font-heading">
              Invirtus
            </span>
          </div>
        </nav>
      </header>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 relative z-10">
        <div className="max-w-lg w-full">
          {status === 'loading' && (
            <div className="text-center py-16">
              <Loader2 className="w-8 h-8 text-[#D9ADFF] animate-spin mx-auto" />
            </div>
          )}

          {status === 'invalid' && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-16"
            >
              <AlertCircle className="w-12 h-12 text-[#E85D5D] mx-auto mb-4" />
              <h1 className="font-heading font-bold text-2xl mb-2">Invalid link</h1>
              <p className="text-white/50">This upload link is invalid or has expired.</p>
            </motion.div>
          )}

          {status === 'ready' && !isUploaded && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="text-center">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#D9ADFF]/30 bg-[#D9ADFF]/10 mb-4">
                  <Sparkles className="w-4 h-4 text-[#D9ADFF]" />
                  <span className="text-[#D9ADFF] text-xs font-semibold tracking-wide uppercase">
                    GITEX Africa Challenge
                  </span>
                </div>
                <h1 className="font-heading font-bold text-2xl sm:text-3xl mb-3">Upload your resume</h1>
                <p className="text-white/50 text-sm sm:text-base">
                  {tokenInfo?.has_cv
                    ? `You already uploaded ${tokenInfo.cv_filename}. You can replace it below.`
                    : `Upload your resume to help us match you with the best opportunities.`
                  }
                </p>
                <p className="text-white/30 text-xs mt-2">Registered as {tokenInfo?.email}</p>
              </div>

              <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] backdrop-blur-sm p-5 sm:p-8 space-y-5">
                {cvFile ? (
                  <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#D9ADFF]/10 border border-[#D9ADFF]/20">
                    <FileText className="w-5 h-5 text-[#D9ADFF] flex-shrink-0" />
                    <span className="text-white/80 text-sm truncate flex-1">{cvFile.name}</span>
                    <Button
                      type="icon"
                      intent="muted"
                      size="xs"
                      icon={<X />}
                      onClick={() => setCvFile(null)}
                      aria-label="Remove resume"
                    />
                  </div>
                ) : (
                  <div
                    onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={`
                      flex flex-col items-center justify-center gap-2 py-10 sm:py-14
                      rounded-xl border-2 border-dashed cursor-pointer transition-all duration-200
                      ${isDragging
                        ? 'border-[#D9ADFF]/50 bg-[#D9ADFF]/5'
                        : 'border-white/10 hover:border-white/20 bg-white/[0.02]'
                      }
                    `}
                  >
                    <Upload className={`w-8 h-8 ${isDragging ? 'text-[#D9ADFF]' : 'text-white/30'}`} />
                    <p className="text-white/40 text-sm text-center">
                      <span className="text-[#D9ADFF] font-medium">Click</span> or drag your resume here
                    </p>
                    <p className="text-white/20 text-xs">PDF, DOC, DOCX -- max 10MB</p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </div>
                )}

                {error && (
                  <p className="text-[#E85D5D] text-sm text-center">{error}</p>
                )}

                <Button
                  type="contained"
                  intent="primary"
                  size="md"
                  label={isUploading ? 'Uploading...' : 'Upload resume'}
                  onClick={handleUpload}
                  disabled={!cvFile || isUploading}
                  loading={isUploading}
                  className="w-full"
                />
              </div>
            </motion.div>
          )}

          {isUploaded && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-16"
            >
              <div className="w-16 h-16 rounded-full bg-[#5DBE7A]/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-8 h-8 text-[#5DBE7A]" />
              </div>
              <h1 className="font-heading font-bold text-2xl sm:text-3xl mb-3">Resume uploaded!</h1>
              <p className="text-white/50 text-base max-w-sm mx-auto">
                We've got your resume. You'll hear from us after GITEX Africa with your AI interview link.
              </p>
            </motion.div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/[0.06] px-4 sm:px-6 lg:px-12 py-6">
        <div className="max-w-lg mx-auto flex items-center justify-center gap-3">
          <img src="/logo/light-logo.png" alt="Invirtus" className="w-5 h-5" />
          <span className="text-white/30 text-xs">Invirtus x GITEX Africa 2026</span>
        </div>
      </footer>
    </div>
  );
};
